import { ChangeDetectionStrategy, Component, EventEmitter, Input, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { OPTION_LOCKED_ID, Plan, Product } from '@bytel/bytel-sales';
import { CheckoutCartModel } from '@bytel/checkout';

import { MainCartService } from '@services/main-cart.service';

import { AddPlanConfig } from '@interfaces/checkout.interface';

import { Observable, finalize, tap } from 'rxjs';

import { CardOfferComponent } from './card-offer/card-offer.component';

@Component({
    selector: 'checkout-card-offer-container',
    templateUrl: './card-offer-container.component.html',
    styleUrl: 'card-offer-container.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [CardOfferComponent],
})
export class CardOfferContainerComponent {
    @Input() public plan: Plan;
    @Input() public quoteIndex: number = 1;
    @Input() public isLoading: boolean = false;
    @Input() public cartForm: FormGroup;
    @Input() public selectPlanEvent: EventEmitter<{
        plan: Plan;
        shouldReset?: boolean;
    }>;
    @Input() public atLeastOneOfferHasPromoHome: boolean = false;
    @Input() public atLeastOneBlockablePlan: boolean = false;

    public isLocked: boolean = false;
    protected cartService: MainCartService = inject(MainCartService);

    public setLocked(plan: Plan, value: boolean): void {
        this.isLocked = value;
        if (this.getQty(plan) === 1) {
            if (this.isLocked) {
                this.addProductEvent({
                    isLocked: true,
                });
            } else {
                this.removeProductEvent(OPTION_LOCKED_ID);
            }
        }
    }

    public addProduct(addPlan: AddPlanConfig): Observable<CheckoutCartModel> {
        this.isLoading = true;
        const gencodesToAdd: string[] = [];
        if (addPlan.plan) {
            gencodesToAdd.push(addPlan.plan.gencode);
        }
        if (addPlan.isLocked) {
            gencodesToAdd.push(OPTION_LOCKED_ID);
        }
        return this.cartService.addProducts(gencodesToAdd).pipe(
            tap(() => {
                if (addPlan.plan) {
                    this.selectPlanEvent.emit({ plan: addPlan.plan });
                }
            }),
            finalize(() => {
                this.isLoading = false;
            }),
        );
    }

    public removeProduct(gencode: string): Observable<Product[]> {
        this.isLoading = true;
        return this.cartService.removeProductsByGencode(gencode, this.quoteIndex).pipe(
            tap(() => {
                const quote = this.cartService.getQuote(this.quoteIndex);
                if (!quote.getProductByType(Plan)) {
                    this.selectPlanEvent.emit({ plan: null });
                }
            }),
            finalize(() => {
                this.isLoading = false;
            }),
        );
    }

    public addProductEvent(addPlan: AddPlanConfig): void {
        this.addProduct(addPlan).subscribe();
    }

    public removeProductEvent(gencode: string): void {
        this.removeProduct(gencode).subscribe();
    }

    protected getQty(plan: Plan): number {
        return this.cartService.getProductQty(plan);
    }
}
